import { FC, RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  faCalendarDays,
  faChevronDown,
  faCircleInfo,
  faHeadset,
  faMobileScreenButton,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'utils/utils';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { isValidProposalCancellationDate, getAdjustedProposalCancellationDate } from 'utils/shared/proposal';
import { buildTripClientSummary } from 'utils/shared/tripBuilderClientSummary';
import { getBlobUrl, resolveGalleryImageUrl } from 'utils/shared/tripBuilderImages';
import { getHotelCancellationSettings } from 'utils/shared/tripResultBuilderPreview';
import { i18nTB } from 'utils/shared/utils';
import { availableLanguages } from 'constants/constant';
import Dropdown from '../../Dropdown';
import { getTripBuilderClientName } from 'utils/shared/tripBuilder';
import { images, imgLogo } from 'constants/generic';

type TripBuilderClientHeader2Props = {
  proposal: ProposalData;
  proposalImage?: string;
  scrollTargetRef?: RefObject<HTMLElement>;
  logoSrc?: string;
  logoAlt?: string;
};

type ProposalMeta = {
  agentName?: string;
  agentEmail?: string;
  agentPhone?: string;
  branchLogo?: string;
};

const parseProposalMeta = (externalParam_1?: string): ProposalMeta => {
  if (!externalParam_1) {
    return {};
  }

  try {
    const params = JSON.parse(externalParam_1);
    return {
      agentName: params.nameFromFax || params.user?.userFullName,
      agentEmail: params.user?.userEmail,
      agentPhone: params.user?.userPhone,
      branchLogo: params.branchLogo,
    };
  } catch {
    return {};
  }
};

const formatVacationDates = (startDate: string | null, endDate: string | null, locale: string) => {
  if (!startDate || !endDate) {
    return null;
  }

  return `${dateFormat(startDate, 'dd.MM', locale)}-${dateFormat(endDate, 'dd.MM.yyyy', locale)}`;
};

const TB2_WAVE_CURVE =
  'M0,100 C130,118 390,120 720,108 C960,96 1050,94 1180,96 C1300,98 1360,104 1440,110';

const TB2_HEADER_FILL =
  'M0,0 H1440 V110 C1360,104 1300,98 1180,96 C1050,94 960,96 720,108 C390,120 130,118 0,100 Z';

const HEADER_SOCIAL_ICONS = [
  { icon: faFacebook, label: 'Facebook' },
  { icon: faInstagram, label: 'Instagram' },
  { icon: faWhatsapp, label: 'WhatsApp' },
] as const;

const TB2_BG_BOTTOM_GAP = 100;

const TripBuilderClientHeader2: FC<TripBuilderClientHeader2Props> = ({
  proposal,
  proposalImage,
  scrollTargetRef,
  logoSrc,
  logoAlt = 'BePro',
}) => {
  
  const blobUrl = getBlobUrl();
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const [langMenuCloseDate, setLangMenuCloseDate] = useState<Date>();

  const currentLanguage =
    availableLanguages.find((item) => i18n.language?.toLowerCase().startsWith(item.lang)) ||
    availableLanguages[0];

  const onChangeLanguage = (lang: string) => {
    void i18n.changeLanguage(lang);
    setLangMenuCloseDate(new Date());
  };

  const summary = useMemo(() => buildTripClientSummary(proposal, t), [proposal, t]);
  const meta = useMemo(() => parseProposalMeta(proposal.externalParam_1), [proposal.externalParam_1]);
  const clientName = useMemo(() => getTripBuilderClientName(proposal), [proposal]);

  const vacationDatesLabel = useMemo(
    () => formatVacationDates(summary.startDate, summary.endDate, i18n.language),
    [summary.startDate, summary.endDate, i18n.language],
  );

  const showFreeCancellation = useMemo(
    () => getHotelCancellationSettings(proposal).showCancellationDate !== false,
    [proposal],
  );

  const freeCancellationDate = useMemo(() => {
    if (!showFreeCancellation) {
      return null;
    }

    const { cancellationDateDelta } = getHotelCancellationSettings(proposal);

    const cxlDates = (proposal.listOfProducts || [])
      .flatMap((product) => product.listOfItems)
      .flatMap((item) => item.hotels || [])
      .flatMap((hotel) => (hotel.rooms || []).map((room) => room.cxl))
      .filter(Boolean) as string[];

    const validDates = cxlDates
      .filter(isValidProposalCancellationDate)
      .map((cxl) => getAdjustedProposalCancellationDate(cxl, +cancellationDateDelta));

    if (!validDates.length) {
      return null;
    }

    const earliest = validDates.sort((a, b) => a.getTime() - b.getTime())[0];

    return dateFormat(earliest, 'dd/MM/yyyy', i18n.language);
  }, [proposal, showFreeCancellation, i18n.language]);

  const scrollToContent = () => {
    scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const bodyRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const syncRafRef = useRef<number>();

  const syncBgHeight = useCallback(() => {
    const body = bodyRef.current;
    const bg = bgRef.current;
    const card = cardRef.current;

    if (!body || !bg || !card) {
      return;
    }

    const bodyTop = body.getBoundingClientRect().top;
    const cardBottom = card.getBoundingClientRect().bottom;
    const height = Math.ceil(cardBottom - bodyTop + TB2_BG_BOTTOM_GAP);

    bg.style.height = `${height}px`;
  }, []);

  const scheduleSyncBgHeight = useCallback(() => {
    if (syncRafRef.current != null) {
      return;
    }

    syncRafRef.current = requestAnimationFrame(() => {
      syncRafRef.current = undefined;
      syncBgHeight();
    });
  }, [syncBgHeight]);

  useLayoutEffect(() => {
    syncBgHeight();

    const body = bodyRef.current;
    const card = cardRef.current;

    if (!body || !card) {
      return;
    }

    const observer = new ResizeObserver(scheduleSyncBgHeight);
    observer.observe(card);
    window.addEventListener('resize', scheduleSyncBgHeight);

    return () => {
      if (syncRafRef.current != null) {
        cancelAnimationFrame(syncRafRef.current);
      }
      observer.disconnect();
      window.removeEventListener('resize', scheduleSyncBgHeight);
    };
  }, [syncBgHeight, scheduleSyncBgHeight, proposal.id, showFreeCancellation, freeCancellationDate, i18n.language]);

  const infoRows = [
    {
      key: 'client',
      icon: faUser,
      label: t('trip_builder.client_name'),
      value: clientName,
    },
    {
      key: 'travellers',
      icon: faUsers,
      label: t('trip_builder.passenger_composition'),
      value: summary.travellersLabel || '–',
    },
    {
      key: 'offer',
      icon: faCircleInfo,
      label: t('trip_builder.offer_number'),
      value: proposal.id ? String(proposal.id) : '–',
    },
    {
      key: 'agent',
      icon: faHeadset,
      label: t('trip_builder.agent_name'),
      value: meta.agentName || '–',
    },
    {
      key: 'agent-details',
      icon: faMobileScreenButton,
      label: t('trip_builder.agent_details'),
      value: [meta.agentPhone, meta.agentEmail].filter(Boolean).join('\n') || '–',
      multiline: true,
    },
    {
      key: 'dates',
      icon: faCalendarDays,
      label: t('trip_builder.vacation_date'),
      value: vacationDatesLabel || '–',
      suffix:
        summary.nights != null
          ? `${summary.nights} ${summary.nights > 1 ? t('nights') : t('night')}`
          : undefined,
    },
  ];

  return (
    <section className="tb2" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
      <div className="tb2-body" ref={bodyRef}>
        <div className="tb2-bg" ref={bgRef} aria-hidden="true">
          {proposalImage ? (
            <img
              className="tb2-bg-image"
              src={resolveGalleryImageUrl(blobUrl, proposalImage)}
              alt=""
            />
          ) : (
            <div className="tb2-bg-fallback" />
          )}
          <div className="tb2-bg-overlay" />
          <div className="tb2-bg-wave" aria-hidden="true">
            <svg className="tb2-bg-wave-fill" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={`${TB2_WAVE_CURVE} L1440,120 L0,120 Z`} />
            </svg>
          </div>
        </div>

        <header className="tb2-header">
          <div className="tb2-header-bar">
            <div className="tb2-header-brand">
              <img
                className="tb2-header-logo"
                src={logoSrc || images?.logo108 || imgLogo}
                alt={logoAlt}
              />
            </div>

            <div className="tb2-header-social">
              <div className="tb2-header-lang">
                <Dropdown
                  byClick
                  openerEmpty
                  appendToBody
                  align={i18n.language === 'he' ? 'left' : 'right'}
                  closeOnClickDate={langMenuCloseDate}
                  dropdownClassName="tb2-header-lang-menu"
                  opener={
                    <button
                      type="button"
                      className="tb2-header-lang-opener"
                      aria-label={currentLanguage.name}
                    >
                      <span>{currentLanguage.lang.toUpperCase()}</span>
                      <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
                    </button>
                  }
                >
                  {availableLanguages.map((item) => (
                    <div
                      className={
                        'c-dropdown-row' +
                        (item.lang === currentLanguage.lang ? ' is-active' : '')
                      }
                      key={item.lang}
                      onClick={() => onChangeLanguage(item.lang)}
                    >
                      {item.name}
                    </div>
                  ))}
                </Dropdown>
              </div>
              {HEADER_SOCIAL_ICONS.map(({ icon, label }) => (
                <span key={label} className="tb2-header-social-icon" aria-label={label}>
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </span>
              ))}
            </div>
          </div>
          <div className="tb2-header-shape" aria-hidden="true">
            <svg className="tb2-header-shape-fill" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={TB2_HEADER_FILL} />
            </svg>
            <svg className="tb2-header-shape-stroke" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={TB2_WAVE_CURVE} fill="none" />
            </svg>
          </div>
        </header>

        <div className="tb2-shell">
          <div className="tb2-card" ref={cardRef}>
            <h1 className="tb2-card-title">{t('proposal_full')}: {proposal.id}</h1>

            <div className="tb2-card-grid">
              {infoRows.map((row) => (
                <div className="tb2-card-cell" key={row.key}>
                  <span className="tb2-card-cell-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={row.icon} />
                  </span>
                  <span className="tb2-card-cell-divider" aria-hidden="true" />

                  <div className="tb2-card-cell-text">
                    <span className="tb2-card-cell-label">{row.label}</span>
                    {row.multiline ? (
                      row.value.split('\n').map((line, index) => (
                        <span className="tb2-card-cell-value" key={`${row.key}-${index}`}>
                          {line}
                        </span>
                      ))
                    ) : (
                      <span className="tb2-card-cell-value">{row.value}</span>
                    )}
                    {!!row.suffix && <span className="tb2-card-cell-value">{row.suffix}</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="tb2-card-footer">
              <button type="button" className="tb2-card-cta" onClick={scrollToContent}>
                {t('trip_builder.scroll_to_itinerary')}
              </button>
            </div>

            <button
              type="button"
              className="tb2-card-scroll"
              onClick={scrollToContent}
              aria-label={t('trip_builder.scroll_to_itinerary')}
            >
              <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripBuilderClientHeader2;
